import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ContentKind } from '~/generated/graphql';
import { allUnselectedState, useIntialState } from './initialState';
import { useFilterParams } from './searchParams';

export interface ContentFilterItems<T> {
  count?: number;
  label: string;
  selected: boolean;
  value: T;
}

export interface ContentFilter<T> {
  items: ContentFilterItems<T>[];
  open: boolean;
}

export interface ContentFilters {
  kind: ContentFilter<ContentKind>;
  tags: ContentFilter<string>;
}

export interface ContentPanel {
  open: boolean;
}

export interface ContentFiltersState {
  filters: ContentFilters;
  panel: ContentPanel;
}

export type ContentFilterArgs = {
  [K in keyof ContentFilters]: { field: K; items: ContentFilters[K]['items'] };
}[keyof ContentFilters];

export const useFilters = (initialTags: readonly string[] | undefined) => {
  const params = useFilterParams();
  const initialState = useIntialState(params);

  const [filter, setFilter] = useState<ContentFiltersState>(initialState);

  useEffect(() => {
    if (!initialTags) return;
    if (filter.filters.tags.items.length === initialTags.length) return;

    setFilter((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        tags: {
          ...prev.filters.tags,
          items: initialTags.map((tag) => ({
            label: tag,
            value: tag,
            selected: params.tags.includes(tag),
          })),
        },
      },
    }));
  }, [filter.filters.tags.items.length, initialTags, params.tags]);

  const [_, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    ({ field, items }: ContentFilterArgs) =>
      setSearchParams((params) => {
        const selected = items.filter((x) => x.selected);
        selected.length > 0
          ? params.set(field, JSON.stringify(selected.map((x) => x.value)))
          : params.delete(field);
        return params;
      }),
    [setSearchParams],
  );

  const clearFilter = useCallback(() => {
    setFilter(() => allUnselectedState);
    setSearchParams((params) => {
      params.delete('kind');
      params.delete('tags');
      params.delete('search');
      return params;
    });
  }, [setSearchParams]);

  const setFilterItems = useCallback(
    (args: ContentFilterArgs) => {
      setFilter((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          [args.field]: {
            ...prev.filters[args.field],
            items: args.items,
          },
        },
      }));
      updateSearchParams(args);
    },
    [updateSearchParams],
  );

  const toggleAccordion = useCallback(
    (field: keyof ContentFilters) =>
      setFilter((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          [field]: {
            ...prev.filters[field],
            open: !prev.filters[field].open,
          },
        },
      })),
    [],
  );

  const togglePanel = useCallback(
    () => setFilter((prev) => ({ ...prev, panel: { open: !prev.panel.open } })),
    [],
  );

  return {
    filter,
    clearFilter,
    setFilterItems,
    toggleAccordion,
    togglePanel,
  };
};
